import { useContext, useState, type FormEvent } from "react";
import { ModalContext } from "../../../store/ModalContext";
import { UsersContext } from "../../../store/UsersContext";
import { useNavigate } from "react-router-dom";
import { AccountRole } from "../../../utils/types";
import { useAuthUser } from "../../../hooks/useAuthUser";

const validationMessage =
  "Please fill out company name, company registration number, email, phone, address, postal code, and city!";

/** Submit with a `<button type="submit" form={USER_BUSINESS_FORM_ID} />` outside this `<form>`. */
export const USER_BUSINESS_FORM_ID = "create-user-business-form";

type UserBusinessFormProps = {
  onSubmitChange: (isSubmitting: boolean) => void;
};
const UserBusinessForm = ({ onSubmitChange }: UserBusinessFormProps) => {
  const { openModal } = useContext(ModalContext);
  const { createUser } = useContext(UsersContext);
  const { isAuthenticated } = useAuthUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const navigate = useNavigate();

  function businessFormIsValid(
    data: Record<string, FormDataEntryValue>,
  ): boolean {
    const companyName = String(data.companyName ?? "").trim();
    const companyRegistrationNumber = String(
      data.companyRegistrationNumber ?? "",
    ).trim();
    const email = String(data.email ?? "").trim();
    const phone = String(data.phone ?? "").trim();
    const address = String(data.address ?? "").trim();
    const postalCode = String(data.postalCode ?? "").trim();
    const city = String(data.city ?? "").trim();
    return Boolean(
      companyName &&
      companyRegistrationNumber &&
      email &&
      phone &&
      address &&
      postalCode &&
      city,
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;

    const fd = new FormData(event.currentTarget);
    const data = Object.fromEntries(fd.entries());

    if (!businessFormIsValid(data)) {
      setFormError(validationMessage);
      return;
    }

    setFormError(null);

    if (!isAuthenticated) {
      openModal({
        body: "You must be logged in (MongoDB) to create users.",
        showCloseButton: true,
      });
      return;
    }

    setIsSubmitting(true);
    onSubmitChange(true);

    try {
      await createUser({
        role: AccountRole.Business,
        companyName: data.companyName as string,
        companyRegistrationNumber: data.companyRegistrationNumber as string,
        email: data.email as string,
        phoneNumber: data.phone as string,
        address: data.address as string,
        postalCode: data.postalCode as string,
        city: data.city as string,
      });
      openModal({
        title: "Success!",
        body: "The account has been created and an email has been sent.",
        showCloseButton: true,
      });
      navigate("/dashboard/admin/manage-users");
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to create user";
      openModal({ body: msg, showCloseButton: true });
    } finally {
      setIsSubmitting(false);
      onSubmitChange(false);
    }
  }

  return (
    <form
      id={USER_BUSINESS_FORM_ID}
      noValidate
      onSubmit={handleSubmit}
      onInput={() => setFormError(null)}
      className="form"
    >
      <div className="form-fields-container">
        <div className="modal-form-input-pair">
          <label htmlFor="company-name" className="sr-only">
            Company Name
          </label>
          <input
            type="text"
            name="companyName"
            id="company-name"
            placeholder="Company Name"
          />
          <label htmlFor="company-registration-number" className="sr-only">
            Company Registration Number
          </label>
          <input
            type="text"
            name="companyRegistrationNumber"
            id="company-registration-number"
            inputMode="numeric"
            maxLength={11}
            placeholder="Company Reg. Number"
          />
        </div>
        <div className="modal-form-input-pair">
          <label htmlFor="email" className="sr-only">
            Email
          </label>
          <input
            type="email"
            name="email"
            id="email"
            placeholder="Email"
          />
          <label htmlFor="phone" className="sr-only">
            Phone Number
          </label>
          <input
            type="phone"
            name="phone"
            id="phone"
            placeholder="Phone"
          />
        </div>
        <div className="user-input-box">
          <label htmlFor="address" className="sr-only">
            Address
          </label>
          <input
            type="text"
            name="address"
            id="address"
            placeholder="Address"
          />
        </div>
        <div className="modal-form-input-pair">
          <label htmlFor="postal-code" className="sr-only">
            Postal Code
          </label>
          <input
            type="text"
            name="postalCode"
            id="postal-code"
            placeholder="Postal Code"
            inputMode="numeric"
            maxLength={6}
            autoComplete="postal-code"
          />
          <label htmlFor="city" className="sr-only">
            City
          </label>
          <input
            type="text"
            name="city"
            id="city"
            placeholder="City"
          />
        </div>
      </div>
      {formError && (
        <p className="pickup-form-validation-msg" role="alert">
          {formError}
        </p>
      )}
    </form>
  );
};

export default UserBusinessForm;
